/**
 * CommunicationRecordsPage - SPO沟通记录管理页面
 *
 * 功能：
 * 1. 邮件沟通记录展示（时间线）
 * 2. 记录邮件通信（运营商操作）
 * 3. S&P问题列表
 * 4. 发行人在线回答
 * 5. 附件管理
 * 6. 邮件内容查看
 *
 * 权限控制：
 * - 运营商：记录邮件、管理沟通
 * - 发行人：回答问题、查看记录
 * - 管理员：完全权限
 * - 其他角色：仅查看
 */

// @ts-nocheck - TODO: Fix type definitions
import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Alert,
  Modal,
  message,
  Spin,
  Timeline,
  Divider,
  Row,
  Col,
  Typography,
  Empty,
  Tag,
  Upload,
  Badge,
  List,
  Comment,
  Avatar,
  Tooltip,
} from 'antd';
import {
  MailOutlined,
  PlusOutlined,
  FileTextOutlined,
  UserOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  PaperClipOutlined,
  EyeOutlined,
  SendOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import {
  useGetSPOQuery,
  useRecordEmailMutation,
  useGetEmailListQuery,
} from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified';
import StandardPageLayout from '../../components/layout/StandardPageLayout';
import { useResolvedSpoId } from './hooks/useResolvedSpoId';

const { Option } = Select;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

interface CommunicationRecordsPageProps {
  projectId?: string;
}

const CommunicationRecordsPage: React.FC<CommunicationRecordsPageProps> = ({
  projectId: propProjectId,
}) => {
  const { resolvedSpoId, isResolving, isResolveError } = useResolvedSpoId({
    projectId: propProjectId,
  });

  // ==================== State管理 ====================
  const [form] = Form.useForm();
  const [recordModalVisible, setRecordModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<any>(null);
  const [attachments, setAttachments] = useState<UploadFile[]>([]);

  // ==================== API Hooks ====================
  const {
    data: spoData,
    isLoading: spoLoading,
    error: spoError,
    refetch: refetchSPO,
  } = useGetSPOQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });

  const {
    data: emailsData,
    isLoading: emailsLoading,
    refetch: refetchEmails,
  } = useGetEmailListQuery(
    { spoId: resolvedSpoId!, params: {} },
    {
      skip: !resolvedSpoId,
    }
  );

  const [recordEmail, { isLoading: recording }] = useRecordEmailMutation();

  // ==================== 权限控制 ====================
  const userRole = 'operator'; // TODO: 从Redux Store获取实际用户角色
  const canRecord = userRole === 'operator' || userRole === 'admin';
  const canAnswer = userRole === 'issuer' || userRole === 'admin';

  // ==================== 邮件类型选项 ====================
  const EMAIL_TYPES = [
    { value: 'received_from_sp', label: '接收自S&P', color: 'blue' },
    { value: 'sent_to_sp', label: '发送给S&P', color: 'green' },
    { value: 'internal_note', label: '内部备注', color: 'orange' },
    { value: 'question', label: 'S&P提问', color: 'purple' },
    { value: 'answer', label: '发行人回答', color: 'cyan' },
  ];

  // ==================== 文件上传配置 ====================
  const uploadProps = {
    name: 'file',
    multiple: true,
    fileList: attachments,
    beforeUpload: (file: any) => {
      const isValidType =
        file.type === 'application/pdf' ||
        file.type.startsWith('application/vnd') ||
        file.type.startsWith('image/');
      if (!isValidType) {
        message.error('只能上传PDF、Word、Excel或图片文件！');
        return Upload.LIST_IGNORE;
      }
      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error('文件大小不能超过10MB！');
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: (info: any) => {
      setAttachments(info.fileList);
    },
    onRemove: (file: any) => {
      setAttachments(prev => prev.filter(item => item.uid !== file.uid));
    },
  };

  // ==================== 记录邮件通信 ====================
  const handleRecordEmail = async () => {
    try {
      const values = await form.validateFields();

      const emailData = {
        direction: values.emailType,
        subject: values.subject,
        content: values.content,
        sender: values.sender,
        receiver: values.receiver,
        attachments: attachments.map(file => ({
          filename: file.name,
          url: 'https://example.com/attachments/' + file.uid, // TODO: 实际文件URL
        })),
      };

      const result = await recordEmail({
        spoId: resolvedSpoId!,
        data: emailData,
      }).unwrap();

      if (result.success) {
        message.success('邮件记录已保存！');
        setRecordModalVisible(false);
        form.resetFields();
        setAttachments([]);
        refetchEmails();
        refetchSPO();
      } else {
        message.error(result.error?.message || '记录失败');
      }
    } catch (error) {
      console.error('Record email error:', error);
      message.error('记录失败，请检查表单');
    }
  };

  // ==================== 查看邮件详情 ====================
  const handleViewEmail = (email: any) => {
    setSelectedEmail(email);
    setViewModalVisible(true);
  };

  // ==================== 渲染时间线 ====================
  const renderTimeline = () => {
    const communications = emailsData?.data?.communications || [];

    if (communications.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="暂无沟通记录"
        />
      );
    }

    return (
      <Timeline mode="left">
        {communications.map((comm: any, index: number) => {
          const emailType = EMAIL_TYPES.find(t => t.value === comm.direction);
          return (
            <Timeline.Item
              key={index}
              color={emailType?.color || 'gray'}
              dot={<MailOutlined />}
              label={
                <div>
                  <Text type="secondary">
                    {dayjs(comm.timestamp).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </div>
              }
            >
              <Card
                size="small"
                hoverable
                onClick={() => handleViewEmail(comm)}
                extra={
                  <Space>
                    <Tag color={emailType?.color}>{emailType?.label}</Tag>
                    {comm.attachments && comm.attachments.length > 0 && (
                      <Badge count={comm.attachments.length}>
                        <PaperClipOutlined style={{ fontSize: 16 }} />
                      </Badge>
                    )}
                  </Space>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div>
                    <Text strong>主题：</Text>
                    <Text>{comm.subject}</Text>
                  </div>
                  <div>
                    <Text strong>发件人：</Text>
                    <Text>{comm.sender}</Text>
                    <Divider type="vertical" />
                    <Text strong>收件人：</Text>
                    <Text>{comm.receiver}</Text>
                  </div>
                  <Paragraph
                    ellipsis={{ rows: 2, expandable: false }}
                    style={{ marginBottom: 0 }}
                  >
                    {comm.content}
                  </Paragraph>
                </Space>
              </Card>
            </Timeline.Item>
          );
        })}
      </Timeline>
    );
  };

  // ==================== 渲染统计卡片 ====================
  const renderStatistics = () => {
    const communications = emailsData?.data?.communications || [];
    const receivedCount = communications.filter(
      (c: any) => c.direction === 'received_from_sp'
    ).length;
    const sentCount = communications.filter(
      (c: any) => c.direction === 'sent_to_sp'
    ).length;
    const questionCount = communications.filter(
      (c: any) => c.direction === 'question'
    ).length;
    const answerCount = communications.filter(
      (c: any) => c.direction === 'answer'
    ).length;

    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">接收邮件</Text>
              <div
                style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}
              >
                {receivedCount}
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">发送邮件</Text>
              <div
                style={{ fontSize: 24, fontWeight: 'bold', color: '#52c41a' }}
              >
                {sentCount}
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">S&P提问</Text>
              <div
                style={{ fontSize: 24, fontWeight: 'bold', color: '#722ed1' }}
              >
                {questionCount}
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">已回答</Text>
              <div
                style={{ fontSize: 24, fontWeight: 'bold', color: '#13c2c2' }}
              >
                {answerCount}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  // ==================== Loading/Error处理 ====================
  if (isResolving || spoLoading || emailsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>加载沟通记录中...</div>
      </div>
    );
  }

  if (spoError || isResolveError || !resolvedSpoId) {
    return (
      <Alert
        message="加载失败"
        description="无法加载SPO流程信息，请刷新页面重试"
        type="error"
        showIcon
      />
    );
  }

  // ==================== 主渲染 ====================
  return (
    <StandardPageLayout
      config={{
        title: 'SPO沟通记录',
        description: 'S&P分析师与发行人之间的邮件沟通记录',
      }}
    >
      {/* 统计卡片 */}
      {renderStatistics()}

      {/* 操作按钮 */}
      {canRecord && (
        <div style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setRecordModalVisible(true)}
          >
            记录邮件通信
          </Button>
        </div>
      )}

      {/* 时间线 */}
      <Card title="沟通时间线" style={{ marginBottom: 24 }}>
        {renderTimeline()}
      </Card>

      {/* 记录邮件Modal */}
      <Modal
        open={recordModalVisible}
        title="记录邮件通信"
        onOk={handleRecordEmail}
        onCancel={() => {
          setRecordModalVisible(false);
          form.resetFields();
          setAttachments([]);
        }}
        okText="保存记录"
        cancelText="取消"
        width={800}
        confirmLoading={recording}
      >
        <Alert
          message="邮件记录说明"
          description="请记录与S&P分析师的邮件往来，包括问题、回答、文档等"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="邮件类型"
                name="emailType"
                rules={[{ required: true, message: '请选择邮件类型' }]}
              >
                <Select placeholder="请选择邮件类型">
                  {EMAIL_TYPES.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="邮件主题"
                name="subject"
                rules={[{ required: true, message: '请输入邮件主题' }]}
              >
                <Input placeholder="请输入邮件主题" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="发件人"
                name="sender"
                rules={[{ required: true, message: '请输入发件人' }]}
              >
                <Input placeholder="如：analyst@spglobal.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="收件人"
                name="receiver"
                rules={[{ required: true, message: '请输入收件人' }]}
              >
                <Input placeholder="如：issuer@company.com" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="邮件内容"
            name="content"
            rules={[
              { required: true, message: '请输入邮件内容' },
              { min: 10, message: '邮件内容至少10个字符' },
            ]}
          >
            <TextArea
              rows={6}
              placeholder="请输入完整的邮件内容"
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <Form.Item label="附件">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持PDF、Word、Excel、图片格式，单个文件不超过10MB
              </p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看邮件详情Modal */}
      <Modal
        open={viewModalVisible}
        title="邮件详情"
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>,
        ]}
        onCancel={() => setViewModalVisible(false)}
        width={800}
      >
        {selectedEmail && (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* 基本信息 */}
              <Card size="small">
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong>邮件类型：</Text>
                    <Tag
                      color={
                        EMAIL_TYPES.find(
                          t => t.value === selectedEmail.direction
                        )?.color
                      }
                    >
                      {
                        EMAIL_TYPES.find(
                          t => t.value === selectedEmail.direction
                        )?.label
                      }
                    </Tag>
                  </Col>
                  <Col span={12}>
                    <Text strong>时间：</Text>
                    <Text>
                      {dayjs(selectedEmail.timestamp).format(
                        'YYYY-MM-DD HH:mm:ss'
                      )}
                    </Text>
                  </Col>
                  <Col span={24}>
                    <Text strong>主题：</Text>
                    <Text>{selectedEmail.subject}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>发件人：</Text>
                    <Text>{selectedEmail.sender}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>收件人：</Text>
                    <Text>{selectedEmail.receiver}</Text>
                  </Col>
                </Row>
              </Card>

              {/* 邮件内容 */}
              <Card size="small" title="邮件内容">
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedEmail.content}
                </Paragraph>
              </Card>

              {/* 附件列表 */}
              {selectedEmail.attachments &&
                selectedEmail.attachments.length > 0 && (
                  <Card size="small" title="附件">
                    <List
                      size="small"
                      dataSource={selectedEmail.attachments}
                      renderItem={(item: any) => (
                        <List.Item
                          actions={[
                            <Button
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={() => window.open(item.url, '_blank')}
                            >
                              查看
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            avatar={
                              <FileTextOutlined style={{ fontSize: 24 }} />
                            }
                            title={item.filename}
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                )}
            </Space>
          </div>
        )}
      </Modal>
    </StandardPageLayout>
  );
};

export default CommunicationRecordsPage;
